import * as React from 'react'
import Dialog from '@material-ui/core/Dialog'
import { useAtom } from 'jotai'
import styled from 'styled-components'
import { X } from '@geist-ui/react-icons'
import { checkoutAtom, crossmintBuyAtom, PurchaseType } from '../../atoms/Wallet'
import { useMediaQuery } from '@material-ui/core'
import CartItem from './SecondaryMarket/CartItem'
import {
  AccountMeta,
  Keypair,
  LAMPORTS_PER_SOL,
  PublicKey,
  sendAndConfirmRawTransaction,
  Transaction,
} from '@solana/web3.js'
import { Button, ButtonGroup, createMuiTheme, ThemeProvider } from '@material-ui/core'
import { rwSolPriceAtom } from '../../atoms/WalletName'
import useAuth from '../../hooks/auth'
import { network } from '../../metadata/2021-12-22/primitives/network'
import { captureException } from '@sentry/nextjs'
import { TokenProgram } from '../../program'
import { useWallet } from '@solana/wallet-adapter-react'
import { Base64 } from 'js-base64'
import { presaleTxSigner, txSigner } from '../../data/TxSigner'
import { useConnectModal } from '../../hooks/walletModal'
import { useTxToast } from '../../hooks/toast'
import { useToasts } from '@geist-ui/react'
import { solanaConnection } from '../../data/Connection'
import { BN } from '@project-serum/anchor'
import { PrimarySaleInfo, VideoData } from '../../interfaces'
import { FbEnrichedUser } from '../../interfaces/User'

// ========================== React component ==================================

const CheckoutDialog = (): JSX.Element => {
  //   const [, setToast] = useToasts()
  const [quantity, setQuantity] = React.useState(1)
  const isMobile = useMediaQuery('(max-width: 43em)')
  const [checkoutInfo, setOpen] = useAtom(checkoutAtom)
  const [price, setPrice] = useAtom(rwSolPriceAtom)
  const [, crossmintBuy] = useAtom(crossmintBuyAtom)
  const { provider, fetchBalance } = useAuth()
  const wallet = useWallet()
  const setVisible = useConnectModal()
  const showTxToast = useTxToast()
  const [, setToast] = useToasts()
  const [video, setVideo] = React.useState<VideoData | undefined>(undefined)
  const [primaryInfo, setPrimaryInfo] = React.useState<PrimarySaleInfo | undefined>(undefined)
  const [creator, setCreator] = React.useState<FbEnrichedUser | undefined>(undefined)

  const getSolPrice = async (): Promise<void> => {
    const resp = await fetch(
      'https://api.coingecko.com/api/v3/simple/price?vs_currencies=usd&ids=solana'
    )
    const json = await resp.json()
    setPrice(json['solana']['usd'] as number)
  }

  React.useEffect(() => {
    setQuantity(1)
    const { video, primaryInfo, creator } = checkoutInfo?.data ?? {}
    setVideo(video)
    setPrimaryInfo(primaryInfo)
    setCreator(creator)
  }, [checkoutInfo])

  React.useEffect(() => {
    if (price === 0) {
      getSolPrice()
    }
  }, [])

  const processingToast = React.useCallback(
    async (text: string): Promise<void> => {
      setToast({
        text,
        type: 'warning',
      })
    },
    [setToast]
  )

  const buyEdition = async (): Promise<void> => {
    if (
      primaryInfo &&
      video &&
      creator &&
      wallet &&
      wallet.connected &&
      wallet.publicKey &&
      provider &&
      wallet.signTransaction
    ) {
      try {
        if (localStorage.getItem('walletName') === '"Crossmint"') {
          crossmintBuy({
            video,
            creatorName: creator.displayName,
            purchaseType: PurchaseType.PRIMARY,

            buyer: wallet.publicKey.toString(),
            seller: video.created_by,
            price: ((primaryInfo?.price ?? 0) / LAMPORTS_PER_SOL).toFixed(2),
            priceLamports: primaryInfo.price,
            tokenSize: quantity,
          })
          setOpen(undefined)
          return
        }

        const mint = new PublicKey(video.address)
        const creatorPubKey = new PublicKey(video.created_by)
        const program = TokenProgram.getProgram(provider)
        const platformTreasury = await TokenProgram.getPlatformTreasuryAddress(program)
        const [metadata] = await TokenProgram.findMetadataAddress(mint)
        const platformSigner = await TokenProgram.getPlatformSignerAddress(program)
        const [tokenAccount] = await TokenProgram.findAssociatedTokenAccountAddress(
          mint,
          wallet.publicKey
        )
        const primaryReceipt = Keypair.generate()

        const transaction = new Transaction({ feePayer: wallet.publicKey })
        transaction.add(
          await program.methods
            .primarySale(quantity, null, primaryInfo?.supply)
            .accounts({
              platformSigner,
              mint,
              metadata,
              platformTreasury,
              tokenAccount,
              primaryReceipt: primaryReceipt.publicKey,
            })
            .remainingAccounts([
              {
                pubkey: platformTreasury,
                isSigner: false,
                isWritable: true,
              },
              {
                pubkey: creatorPubKey,
                isSigner: false,
                isWritable: true,
              },
            ] as AccountMeta[])
            .instruction()
        )

        transaction.recentBlockhash = (await solanaConnection.getLatestBlockhash()).blockhash

        transaction.partialSign(primaryReceipt)

        const encoded = Base64.fromUint8Array(
          transaction.serialize({ requireAllSignatures: false, verifySignatures: true }),
          false
        )
        const adminSignedIx = await txSigner(encoded)

        const tx = Transaction.from(Base64.toUint8Array(adminSignedIx.tx_data))

        const signed = await wallet.signTransaction(tx)

        const fee = await tx.getEstimatedFee(solanaConnection)

        const walletBalance = await fetchBalance()

        if (walletBalance < fee + quantity * primaryInfo.price) {
          setToast({
            text: `Whoops, you don't have enough SOL!`,
            type: 'error',
          })
          return
        }

        const serialized = signed.serialize()

        processingToast('Buying video NFT...')

        setOpen(undefined)

        const hash = await sendAndConfirmRawTransaction(solanaConnection, serialized, {
          commitment: 'confirmed',
          preflightCommitment: 'confirmed',
        })

        setOpen(undefined)

        showTxToast({
          nw:
            process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
              ? network.SOL_MAINNET
              : network.SOL_DEVNET,
          hash: hash,
          text: 'Congrats, you just bought this NFT!',
        })
      } catch (err) {
        console.error(err)
        setToast({
          text: `Oops, something went wrong (${err}). Please try again.`,
          type: 'error',
        })
        captureException(err)
      }
    } else {
      setVisible(true)
    }
  }

  const buyPresaleEdition = async (): Promise<void> => {
    if (
      video &&
      wallet &&
      wallet.connected &&
      wallet.publicKey &&
      provider &&
      checkoutInfo &&
      wallet.signTransaction
    ) {
      try {
        const mint = new PublicKey(video.address)
        const creator = new PublicKey(video.created_by)
        const program = TokenProgram.getProgram(provider)
        const platformTreasury = await TokenProgram.getPlatformTreasuryAddress(program)
        const [metadata] = await TokenProgram.findMetadataAddress(mint)
        const platformSigner = await TokenProgram.getPlatformSignerAddress(program)
        const [tokenAccount] = await TokenProgram.findAssociatedTokenAccountAddress(
          mint,
          wallet.publicKey
        )
        const primaryReceipt = Keypair.generate()

        const transaction = new Transaction({ feePayer: wallet.publicKey })
        transaction.add(
          await program.methods
            .primarySale(
              quantity,
              checkoutInfo.allowlistDiscount
                ? new BN(Math.sqrt(checkoutInfo.allowlistDiscount * quantity))
                : null,
              checkoutInfo.allowlistAmount
            )
            .accounts({
              platformSigner,
              mint,
              metadata,
              platformTreasury,
              tokenAccount,
              primaryReceipt: primaryReceipt.publicKey,
            })
            .remainingAccounts([
              {
                pubkey: platformTreasury,
                isSigner: false,
                isWritable: true,
              },
              {
                pubkey: creator,
                isSigner: false,
                isWritable: true,
              },
            ] as AccountMeta[])
            .instruction()
        )

        transaction.recentBlockhash = (await solanaConnection.getLatestBlockhash()).blockhash

        transaction.partialSign(primaryReceipt)

        const encoded = Base64.fromUint8Array(
          transaction.serialize({ requireAllSignatures: false, verifySignatures: true }),
          false
        )
        const adminSignedIx = await presaleTxSigner(encoded, video.address)

        const tx = Transaction.from(Base64.toUint8Array(adminSignedIx.tx_data))

        const signed = await wallet.signTransaction(tx)

        const fee = await tx.getEstimatedFee(solanaConnection)

        const walletBalance = await fetchBalance()

        if (walletBalance < fee) {
          setToast({
            text: `Whoops, you don't have enough SOL!`,
            type: 'error',
          })
          return
        }

        const serialized = signed.serialize()

        setOpen(undefined)

        processingToast('Buying video NFT...')

        const hash = await sendAndConfirmRawTransaction(solanaConnection, serialized, {
          commitment: 'confirmed',
          //   preflightCommitment: 'confirmed',
          skipPreflight: true,
        })

        setOpen(undefined)

        showTxToast({
          nw:
            process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
              ? network.SOL_MAINNET
              : network.SOL_DEVNET,
          hash: hash,
          text: 'Congrats, you just bought this NFT!',
        })
      } catch (err) {
        console.error(err)
        setToast({
          text: `Oops, something went wrong (${err}). Please try again.`,
          type: 'error',
        })
        captureException(err)
      }
    } else {
      setVisible(true)
    }
  }

  return (
    <Dialog
      onClose={() => {
        console.log('Closed')
      }}
      aria-labelledby="crossmint-dialog"
      open={checkoutInfo !== undefined}
      PaperProps={{
        style: {
          backgroundColor: '#101010',
          borderRadius: 15,
          minWidth: isMobile ? '90vw' : '500px',
          width: isMobile ? '90vw' : '50vh',
          borderWidth: 1,
          borderColor: '#262626',
          borderStyle: 'solid',
          minHeight: '400px',
          height: '30vh',
        },
      }}
      BackdropProps={{
        style: {
          backdropFilter: 'blur(20px)',
        },
      }}
    >
      <OuterDiv>
        <CloseButton color="white" size={'30px'} onClick={() => setOpen(undefined)} />
        <Title>Checkout</Title>
        {video && primaryInfo && creator && (
          <CartItem
            videoTitle={video.title}
            creatorName={creator.displayName}
            videoThumbnailUrl={'https://us.glassapi.xyz/' + video.thumbnail_uri}
            floorPrice={`${
              checkoutInfo?.allowlistDiscount
                ? checkoutInfo.allowlistDiscount / LAMPORTS_PER_SOL
                : primaryInfo.price / LAMPORTS_PER_SOL
            }`}
            primary={true}
          />
        )}
        <InnerDiv>
          <TotalDiv>
            {primaryInfo && (
              <PriceDiv>
                <MainStat>{`${
                  ((checkoutInfo?.allowlistDiscount
                    ? checkoutInfo.allowlistDiscount
                    : primaryInfo.price) *
                    quantity) /
                  LAMPORTS_PER_SOL
                } SOL`}</MainStat>
                <Dollars>{`$${Number(
                  (price *
                    (checkoutInfo?.allowlistDiscount
                      ? checkoutInfo.allowlistDiscount
                      : primaryInfo.price) *
                    quantity) /
                    LAMPORTS_PER_SOL
                ).toFixed(2)}`}</Dollars>
              </PriceDiv>
            )}

            <ThemeProvider theme={theme}>
              <ButtonGroup
                variant="outlined"
                size="large"
                color="default"
                aria-label="outlined button group"
              >
                <Button disabled={quantity === 1} onClick={() => setQuantity(quantity - 1)}>
                  -
                </Button>
                <Button
                  disableTouchRipple
                  disableFocusRipple
                  disableRipple
                  disableElevation
                  style={{ backgroundColor: 'transparent', cursor: 'default' }}
                  color="default"
                >
                  {quantity}
                </Button>
                {primaryInfo && checkoutInfo && (
                  <Button
                    disabled={
                      checkoutInfo.allowlistAmount > 0
                        ? quantity >= checkoutInfo.allowlistAmount - checkoutInfo.balance
                        : quantity >= primaryInfo?.supply - primaryInfo?.sold
                    }
                    onClick={() => setQuantity(quantity + 1)}
                  >
                    +
                  </Button>
                )}
              </ButtonGroup>
            </ThemeProvider>
          </TotalDiv>
          <BuyButton
            disabled={
              !checkoutInfo ||
              (checkoutInfo &&
                checkoutInfo.allowlistAmount > 0 &&
                checkoutInfo.allowlistAmount <= checkoutInfo.balance)
            }
            onClick={() =>
              !checkoutInfo ||
              (checkoutInfo &&
                checkoutInfo.allowlistAmount > 0 &&
                checkoutInfo.allowlistAmount <= checkoutInfo.balance)
                ? null
                : checkoutInfo && !checkoutInfo?.isLive && checkoutInfo?.allowlistAmount > 0
                ? buyPresaleEdition()
                : buyEdition()
            }
          >
            {!checkoutInfo ||
            (checkoutInfo &&
              checkoutInfo.allowlistAmount > 0 &&
              checkoutInfo.allowlistAmount <= checkoutInfo.balance)
              ? `You've already claimed ${checkoutInfo?.allowlistAmount} editions`
              : `Buy ${quantity > 1 ? ` ${quantity} ` : ''}Edition${quantity > 1 ? 's' : ''}`}
          </BuyButton>
        </InnerDiv>
      </OuterDiv>
    </Dialog>
  )
}

export default CheckoutDialog

// ========================== Styled components ================================

const theme = createMuiTheme({
  palette: {
    type: 'dark',
  },
})

const OuterDiv = styled.div`
  width: calc(100% - 100px);
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  margin: 50px;
  margin-top: 0px;
`

const InnerDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100% + 40px);
  gap: 10px;
`

const Title = styled.div`
  color: white;
  font-size: 28px;
  font-weight: 600;
  text-align: center;
  padding: 25px 0;

  @media (max-width: 43em) {
    font-size: 24px;
  }
`

const CloseButton = styled(X)`
  right: 4%;
  top: 4%;
  position: absolute;
  cursor: pointer;
`

const BuyButton = styled.div<{ disabled?: boolean }>`
  background: ${(props) => (props.disabled ? '#323232' : '#FFF')};
  width: calc(100% - 20px);
  border-radius: 10px;
  color: ${(props) => (props.disabled ? '#cdcdcd' : 'black')};
  text-align: center;
  align-items: center;
  font-weight: 700;
  font-size: 15px;
  padding: 15px;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  @media (min-width: 62em) {
    font-size: 16px;
  }
`

const TotalDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 15px 0;
`

const PriceDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`

const Dollars = styled.div`
  color: #cdcdcd;
  font-size: 12px;
  font-weight: 500;
  text-align: left;
  margin-top: -2px;
`

const MainStat = styled.div`
  color: white;
  font-size: 20px;
  font-weight: 700;
  text-align: left;
  @media (min-width: 43em) {
    font-size: 18px;
  }
`
